import type { ReactNode } from "react"
import { IconCircleInfoOutline18 as InfoIcon } from "nucleo-ui-outline-18"
import {
	Line,
	LineChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts"

import { cn } from "@productlane/lib/src/browser"
import { Tooltip as UiTooltip } from "@productlane/ui"

import type { ReportingTone } from "./theme"
import { ReportingChartTooltip } from "./chart-tooltip"
import {
	reportingCardClassName,
	reportingCardLoadingClassName,
	toneColor,
} from "./theme"

export interface MetricTrendPoint {
	date: string
	value: number | null
}

interface ReportingMetricTrendCardProps {
	title: string
	description?: string
	value: ReactNode
	data: MetricTrendPoint[]
	isLoading?: boolean
	tone?: ReportingTone
	valueFormatter?: (value: number) => string
	className?: string
	connectNulls?: boolean
	minPoints?: number
}

export const ReportingMetricTrendCard = ({
	title,
	description,
	value,
	data,
	isLoading,
	tone = "primary",
	valueFormatter = (v) => String(v),
	className,
	connectNulls = false,
	minPoints = 1,
}: ReportingMetricTrendCardProps) => {
	const color = toneColor(tone)
	const maxValue = data.reduce((max, point) => {
		if (point.value == null) return max
		return Math.max(max, point.value)
	}, 0)
	const yMax = maxValue <= 0 ? 1 : maxValue * 1.15
	const plottableCount = data.filter((point) => point.value != null).length
	const hasPlottablePoints = plottableCount >= minPoints

	if (isLoading) {
		return (
			<div className={cn(reportingCardLoadingClassName, "p-5", className)}>
				<div className="mb-2 h-4 w-36 rounded bg-gray-200 dark:bg-gray-700" />
				<div className="mb-4 h-9 w-24 rounded bg-gray-200 dark:bg-gray-700" />
				<div className="h-28 w-full rounded bg-gray-100/80 dark:bg-gray-800/50" />
			</div>
		)
	}

	return (
		<div className={cn(reportingCardClassName, "p-5", className)}>
			<div className="mb-1 flex items-center gap-1.5">
				<h3 className="text-sm font-medium text-secondary">{title}</h3>
				{description ? (
					<UiTooltip label={description}>
						<span className="inline-flex text-tertiary">
							<InfoIcon className="h-3.5 w-3.5" />
						</span>
					</UiTooltip>
				) : null}
			</div>
			<p className="mb-3 text-3xl font-semibold text-primary">{value}</p>
			<div className="h-28 w-full [&_*]:outline-none">
				{!hasPlottablePoints ? (
					<div className="flex h-full items-center justify-center text-xs text-tertiary">
						{plottableCount > 0
							? "Not enough points for a trend"
							: "No data in this range"}
					</div>
				) : (
					<ResponsiveContainer width="100%" height="100%">
						<LineChart
							data={data}
							margin={{ top: 8, right: 4, left: 4, bottom: 2 }}
						>
							<XAxis dataKey="date" hide />
							<YAxis hide domain={[0, yMax]} />
							<Tooltip
								cursor={{
									stroke: "currentColor",
									strokeOpacity: 0.2,
									strokeWidth: 1,
								}}
								content={
									<ReportingChartTooltip
										formatter={(v) => [valueFormatter(v), title]}
									/>
								}
							/>
							<Line
								type="linear"
								dataKey="value"
								name={title}
								stroke={color}
								strokeWidth={2}
								dot={false}
								connectNulls={connectNulls}
								activeDot={{
									r: 3.5,
									strokeWidth: 0,
									fill: color,
								}}
								isAnimationActive={false}
							/>
						</LineChart>
					</ResponsiveContainer>
				)}
			</div>
		</div>
	)
}
